import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { useLocation } from "react-router-dom";
import members from "../content/members.json";
import FlipCard from "../components/WhoWeAre/FlipCard";

const slugify = (s) =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

const SECTIONS = [
  { title: "Trustees", role: "Trustee" },
  { title: "Clinicians", role: "Clinician" },
];

const Section = ({
  title,
  roleFilter,
  flipTargetSlug,
  onFlipComplete,
  elRefs,
  ownerBySlug,
}) => {
  const theme = useTheme();
  const list = members.filter((member) => member.roles.includes(roleFilter));

  useEffect(() => {
    if (!flipTargetSlug) return;
    const el = elRefs.current[flipTargetSlug];
    if (el) el.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "center" });
  }, [flipTargetSlug, elRefs]);

  return (
    <Box component="section" sx={{ mt: { xs: 5, md: 7 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "end",
          justifyContent: "space-between",
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            color={theme.palette.primary.main}
            sx={{ fontWeight: 900 }}
          >
            {list.length} members
          </Typography>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              color: theme.palette.primary.main,
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontStyle: "italic",
              fontWeight: 500,
              lineHeight: 1.1,
            }}
          >
            {title}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))",
          },
          justifyItems: "center",
          alignItems: "stretch",
          gap: 2.5,
        }}
      >
        {list.map((member) => {
          const slug = slugify(member.name);
          const isOwner = ownerBySlug[slug] === roleFilter;
          const shouldFlip = isOwner && flipTargetSlug === slug;
          const ownerProps = isOwner
            ? {
                id: slug,
                ref: (el) => (elRefs.current[slug] = el),
                sx: { scrollMarginTop: 96 },
              }
            : {};

          return (
            <Box
              key={`${title}-${slug}`}
              {...ownerProps}
              sx={{
                width: "100%",
                maxWidth: 340,
                height: 470,
                ...(ownerProps.sx || {}),
              }}
            >
              <FlipCard
                {...member}
                shouldFlip={shouldFlip}
                onFlipped={onFlipComplete}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const WhoWeAre = () => {
  const theme = useTheme();
  const location = useLocation();
  const [flipTargetSlug, setFlipTargetSlug] = useState(null);
  const elRefs = useRef({});

  const ownerBySlug = useMemo(() => {
    const map = {};
    for (const { role } of SECTIONS) {
      for (const member of members) {
        if (member.roles.includes(role)) {
          const slug = slugify(member.name);
          if (!map[slug]) map[slug] = role;
        }
      }
    }
    return map;
  }, []);

  useEffect(() => {
    const hash = location.hash?.replace(/^#/, "") || "";
    const sp = new URLSearchParams(location.search);
    const queryMember = sp.get("member") || "";
    const target =
      hash?.length > 0 ? hash : queryMember ? slugify(queryMember) : "";
    setFlipTargetSlug(target || null);
  }, [location]);

  const handleFlipComplete = useCallback(() => {
    setFlipTargetSlug(null);
  }, []);

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Box
        sx={{
          px: { xs: 2, sm: 4 },
          py: { xs: 6, md: 8 },
          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          color: theme.palette.common.white,
          textAlign: "center",
        }}
      >
        <Chip
          label="The Cyrenaeus team"
          sx={{
            bgcolor: "rgba(255,255,255,0.1)",
            color: theme.palette.common.white,
            border: "1px solid rgba(255,255,255,0.2)",
            mb: 2,
          }}
        />
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: { xs: "2.5rem", md: "4rem" },
          }}
        >
          Who We Are
        </Typography>
        <Typography
          sx={{
            maxWidth: 760,
            mx: "auto",
            mt: 2,
            color: "rgba(255,255,255,0.84)",
            lineHeight: 1.8,
            fontSize: { xs: "1rem", md: "1.08rem" },
          }}
        >
          A voluntary group of clinicians, trustees and advisors working with
          partner hospitals to provide reconstructive surgery and training.
        </Typography>
      </Box>
      <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 2, sm: 4 }, pb: 8 }}>
        {SECTIONS.map(({ title, role }) => (
          <Section
            key={role}
            title={title}
            roleFilter={role}
            flipTargetSlug={flipTargetSlug}
            onFlipComplete={handleFlipComplete}
            elRefs={elRefs}
            ownerBySlug={ownerBySlug}
          />
        ))}
      </Box>
    </Box>
  );
};

export default WhoWeAre;
